/*
 * Sticky add to cart bar - always visible, and its button actually works
 * ---------------------------------------------------------------------
 *
 * PART 1 - VISIBLE FROM PAGE LOAD
 *   <hdt-sticky-btn-atc> parks off-screen at translate3d(0, 100%, 0) and only
 *   slides up once the theme adds .sticky-shown, which happens after the
 *   shopper scrolls past the main Add to cart and Buy It Now buttons. A shopper
 *   who lands, reads the price and decides immediately has no buy button on
 *   screen and has to hunt for one.
 *
 *   CSS alone was not enough. An author !important rule beats a plain inline
 *   style but NOT an inline style set with priority, and global.min.js (170KB,
 *   minified) appears to write transform onto the element directly. This sets
 *   the same inline priority itself and re-applies if the theme writes over it.
 *   assets/hdt-sticky-atc.css still sets the resting position so the bar does
 *   not flash up from the bottom on first paint.
 *
 * PART 2 - THE BUTTON NO LONGER REDIRECTS TO /cart
 *   The sticky bar was the last add path still landing shoppers on the cart
 *   page. The cause is a bug in sections/main-product.liquid:
 *
 *     assign product_form_id_sticky = 'form-product-sticky' | append: product.id
 *     <hdt-sticky-btn-atc form="{{ product_form_id }}">
 *       {%- form 'product', product, id: product_form_id_sticky ... -%}
 *
 *   The custom element is pointed at the MAIN product form, while the form
 *   inside it carries a different id. The theme's handler binds to the form the
 *   attribute names, so the sticky form's submit is never claimed.
 *
 *   tradeuno-cart-page-mode.js catches unclaimed submits, but a form submitted
 *   by script via form.submit() fires no submit event at all, so no submit
 *   listener can stop it. This intercepts the CLICK instead, in the capture
 *   phase, and performs the add over fetch.
 *
 *   THE ROOT FIX would be changing that form attribute to product_form_id_sticky
 *   in the section. Not done here because it is unclear what else the theme
 *   drives off that attribute - variant syncing between the two forms most
 *   likely. If the attribute is ever corrected, delete PART 2 and PART 3 and
 *   the sticky bar goes back through the theme's normal handler.
 *
 * PART 3 - METRES, NOT QUARTER METRES (2026-09-03)
 *   PART 2 shipped a real bug: adding 1 metre put 0.25 m in the cart.
 *
 *   Fabric is stocked in 0.25 m units, so the visible "1 Meter" has to reach
 *   Shopify as a quantity of 4. assets/tradeuno-fabric-meter.js does that
 *   conversion on click in the CAPTURE phase, writing chunks into the input and
 *   restoring the metre display on the next tick.
 *
 *   Both its listener and the one below are registered on `document` in the
 *   capture phase, so they run in registration order - and this file loads
 *   first. It serialised the form while the input still read "1", and the
 *   conversion to "4" happened afterwards, on a FormData that had already been
 *   built. One metre went in as one chunk.
 *
 *   Rather than depend on listener ordering, the conversion is applied to the
 *   FormData here, using the same maths as fabric-meter.js (round(metres/0.25),
 *   floored at the variant's minimum chunk count). The DOM input is never
 *   touched, so the metre display the shopper sees is unaffected.
 *
 *   If fabric-meter.js got there first, its tradeunoConverting flag is set and
 *   the input already holds chunks - in that case the value is used as-is, so
 *   the two can never both convert and turn 1.5 m into 24 chunks.
 *
 * TO REVERT: delete the <script> tag for this file from layout/theme.liquid.
 */
(function () {
  var SEL = '.hdt-sticky-btn-atc';
  var SEL_SUBMIT = '.hdt-sticky-atc__submit';
  var SHOWN_CLASS = 'sticky-shown';
  var REAPPLY_LIMIT = 30;
  var CHUNK = 0.25;

  var writing = false;
  var reapplies = 0;
  var observers = [];

  function root() {
    var base = '/';
    try {
      if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
        base = window.Shopify.routes.root;
      }
    } catch (e) {}
    if (base.charAt(base.length - 1) === '/') base = base.slice(0, -1);
    return base;
  }

  /* ---- Part 1: keep the bar on screen ------------------------------------ */

  function stop() {
    for (var i = 0; i < observers.length; i++) {
      try {
        observers[i].disconnect();
      } catch (e) {}
    }
    observers = [];
  }

  function force(el) {
    if (!el) return;

    writing = true;

    if (el.hasAttribute('hidden')) el.removeAttribute('hidden');

    el.style.setProperty('transform', 'translate3d(0, 0, 0)', 'important');
    el.style.setProperty('visibility', 'visible', 'important');
    el.style.setProperty('opacity', '1', 'important');

    document.documentElement.classList.add(SHOWN_CLASS);
    if (document.body) document.body.classList.add(SHOWN_CLASS);

    window.setTimeout(function () {
      writing = false;
    }, 0);
  }

  function watch(el) {
    if (!window.MutationObserver) return;

    var onChange = function () {
      if (writing) return;
      reapplies++;
      if (reapplies > REAPPLY_LIMIT) {
        stop();
        return;
      }
      force(el);
    };

    var elObserver = new MutationObserver(onChange);
    elObserver.observe(el, {
      attributes: true,
      attributeFilter: ['style', 'class', 'hidden']
    });
    observers.push(elObserver);

    var classObserver = new MutationObserver(onChange);
    classObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    if (document.body) {
      classObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['class']
      });
    }
    observers.push(classObserver);
  }

  /* ---- Part 3: metres -> quarter-metre chunks ----------------------------- */

  // Mirrors metersToChunks() in assets/tradeuno-fabric-meter.js. The quantity
  // input lives OUTSIDE the form and is linked by the form attribute, so it is
  // reached through form.elements - form.querySelector would not find it.
  function applyFabricQuantity(form, fd) {
    var mult = parseInt((form.dataset && form.dataset.tradeunoFabricChunksPerMeter) || '1', 10);
    if (!(mult >= 4)) return fd;

    var els = Array.prototype.slice.call(form.elements || []);
    for (var i = 0; i < els.length; i++) {
      var inp = els[i];
      if (!(inp instanceof HTMLInputElement)) continue;
      if (inp.dataset.tradeunoMeterQty !== 'true') continue;
      if (inp.name === 'updates[]') continue;

      // fabric-meter.js already converted this input - value is chunks already.
      if (inp.dataset.tradeunoConverting === '1') return fd;

      var minChunks = parseInt(inp.dataset.tradeunoMinChunks || '1', 10);
      var meters = parseFloat(inp.value);
      if (isNaN(meters)) meters = minChunks * CHUNK;

      var chunks = Math.max(minChunks, Math.round(meters / CHUNK));
      fd.set(inp.name || 'quantity', String(chunks));
      return fd;
    }

    return fd;
  }

  /* ---- Part 2: own the add ------------------------------------------------ */

  // Distinct products, matching snippets/header_group_icons.liquid and
  // tradeuno-cart-page-mode.js. Fabric is stored in 0.25 m units, so a unit
  // count would read 4 after a 1 metre add.
  function paintBadge(count) {
    var nodes = document.querySelectorAll('hdt-cart-count');
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].textContent = String(count);
    }
    var fav = document.querySelector('favicon-cart-badge');
    if (fav) fav.setAttribute('cart-count', String(count));
  }

  function refreshBadge() {
    return fetch(root() + '/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        paintBadge((cart.items || []).length);
      })
      .catch(function () {});
  }

  function setLoading(btn, on) {
    var spinner = btn.querySelector('.hdt-loading__spinner');
    var label = btn.querySelector('.hdt-btn-atc_text');
    if (spinner) {
      if (on) spinner.removeAttribute('hidden');
      else spinner.setAttribute('hidden', '');
    }
    if (label) label.style.opacity = on ? '0.35' : '';
    btn.disabled = !!on;
    if (on) btn.setAttribute('aria-busy', 'true');
    else btn.removeAttribute('aria-busy');
  }

  function flash(btn, message) {
    var label = btn.querySelector('.hdt-btn-atc_text');
    if (!label) return;
    var original = label.textContent;
    label.textContent = message;
    window.setTimeout(function () {
      label.textContent = original;
    }, 2500);
  }

  document.addEventListener(
    'click',
    function (e) {
      var t = e.target;
      if (!t || typeof t.closest !== 'function') return;

      var btn = t.closest(SEL_SUBMIT);
      if (!btn || btn.disabled) return;

      var form = btn.form || btn.closest('form');
      if (!form) return;

      // Take full ownership: the theme's handler is bound to the wrong form and
      // anything it does with this click ends in a page load.
      e.preventDefault();
      e.stopPropagation();

      setLoading(btn, true);

      fetch(root() + '/cart/add.js', {
        method: 'POST',
        body: applyFabricQuantity(form, new FormData(form)),
        headers: { Accept: 'application/json' }
      })
        .then(function (res) {
          return res.json().then(function (data) {
            if (!res.ok) throw new Error(data.description || data.message || '');
            return data;
          });
        })
        .then(function () {
          return refreshBadge();
        })
        .catch(function (err) {
          // Stock limits are the common case here - fabric carries real
          // inventory caps, so a silent no-op would look like a dead button.
          flash(btn, err && err.message ? err.message : 'Could not add');
          return refreshBadge();
        })
        .then(function () {
          setLoading(btn, false);
        });
    },
    true
  );

  /* ---- boot --------------------------------------------------------------- */

  function build() {
    var el = document.querySelector(SEL);
    if (!el) return false;
    if (el.__tuStickyAlways) return true;
    el.__tuStickyAlways = true;

    force(el);
    watch(el);
    return true;
  }

  function init() {
    if (build()) return;

    var tries = 0;
    var timer = window.setInterval(function () {
      tries++;
      if (build() || tries > 20) window.clearInterval(timer);
    }, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function () {
    reapplies = 0;
    stop();
    init();
  });
})();
